import * as React from "react";
import { AlertCircle, Loader2, Wallet } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useTerms } from "@/hooks/useTerms";
import {
  formatPaise,
  paiseToRupees,
  rupeesToPaise,
  useAddPayslip,
  useUpdatePayslip,
  type CreatePayslipBody,
  type Payslip,
  type UpdatePayslipBody,
} from "@/lib/payroll/api";

/**
 * Phase SC-2c — payslip create / edit drawer (draft runs only).
 *
 * A payslip is one team member's line on a monthly pay run: gross pay minus
 * deductions = net. Amounts entered in RUPEES, stored/sent as integer paise.
 * The parent only mounts this when the run is a draft and the user has
 * `manage_payroll`; the BE re-checks both.
 */

interface DraftState {
  staffName: string;
  roleLabel: string;
  grossRupees: string;
  deductionRupees: string;
  note: string;
}

function emptyDraft(): DraftState {
  return { staffName: "", roleLabel: "", grossRupees: "", deductionRupees: "", note: "" };
}

function draftFromExisting(existing: Payslip | null | undefined): DraftState {
  if (!existing) return emptyDraft();
  return {
    staffName: existing.staff_name,
    roleLabel: existing.role_label,
    grossRupees: String(paiseToRupees(existing.gross_paise)),
    deductionRupees:
      existing.deduction_paise > 0 ? String(paiseToRupees(existing.deduction_paise)) : "",
    note: existing.note ?? "",
  };
}

function trimmedOrNull(v: string): string | null {
  const t = v.trim();
  return t.length > 0 ? t : null;
}

export interface PayslipEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  existing?: Payslip | null;
  onSaved?: () => void;
}

export function PayslipEditorSheet({
  open,
  onOpenChange,
  runId,
  existing,
  onSaved,
}: PayslipEditorSheetProps) {
  const isEdit = Boolean(existing);
  const { ct } = useTerms();
  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(draftFromExisting(existing));
      setSubmitError(null);
    }
  }, [open, existing]);

  const addMutation = useAddPayslip();
  const updateMutation = useUpdatePayslip();
  const isSubmitting = addMutation.isPending || updateMutation.isPending;

  const set = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const grossPaise = rupeesToPaise(draft.grossRupees);
  // Deduction is optional; empty string → 0. A non-empty invalid value → null (blocks submit).
  const deductionPaise =
    draft.deductionRupees.trim() === "" ? 0 : (rupeesToPaise(draft.deductionRupees) ?? -1);
  const deductionExceedsGross =
    grossPaise !== null && deductionPaise >= 0 && deductionPaise >= grossPaise;

  const canSubmit =
    draft.staffName.trim().length > 0 &&
    draft.roleLabel.trim().length > 0 &&
    grossPaise !== null &&
    grossPaise > 0 &&
    deductionPaise >= 0 &&
    !deductionExceedsGross &&
    !isSubmitting;

  const netPaise = grossPaise !== null && deductionPaise >= 0 ? grossPaise - deductionPaise : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit || grossPaise === null) return;

    try {
      if (existing) {
        const body: UpdatePayslipBody = {};
        const name = draft.staffName.trim();
        const role = draft.roleLabel.trim();
        if (name !== existing.staff_name) body.staff_name = name;
        if (role !== existing.role_label) body.role_label = role;
        if (grossPaise !== existing.gross_paise) body.gross_paise = grossPaise;
        if (deductionPaise !== existing.deduction_paise) body.deduction_paise = deductionPaise;
        if (trimmedOrNull(draft.note) !== (existing.note ?? null))
          body.note = trimmedOrNull(draft.note);

        if (Object.keys(body).length === 0) {
          onOpenChange(false);
          return;
        }
        await updateMutation.mutateAsync({ runId, payslipId: existing.id, body });
        toast.success(`Saved ${name}'s payslip.`);
      } else {
        const body: CreatePayslipBody = {
          staff_name: draft.staffName.trim(),
          role_label: draft.roleLabel.trim(),
          gross_paise: grossPaise,
          deduction_paise: deductionPaise,
          note: trimmedOrNull(draft.note),
        };
        await addMutation.mutateAsync({ runId, body });
        toast.success(`Added ${body.staff_name} to this run.`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || "Couldn't save. Please retry."
          : err instanceof Error
            ? err.message
            : "Couldn't save. Please retry.";
      setSubmitError(message);
    }
  };

  const title = isEdit ? "Edit payslip" : "Add payslip";
  const description = isEdit
    ? `Update ${existing?.staff_name ?? "this payslip"}.`
    : ct("Add a staff member to this month's pay run.");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[460px]"
        data-testid="payslip-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <Wallet className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">{title}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">{description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <Field label="Name" htmlFor="ps-name" required>
              <Input
                id="ps-name"
                value={draft.staffName}
                maxLength={200}
                required
                autoFocus
                onChange={(e) => set("staffName", e.target.value)}
                placeholder="Ramesh Kumar"
              />
            </Field>

            <Field label="Role" htmlFor="ps-role" required>
              <Input
                id="ps-role"
                value={draft.roleLabel}
                maxLength={80}
                required
                onChange={(e) => set("roleLabel", e.target.value)}
                placeholder="Front desk"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Gross pay (₹)" htmlFor="ps-gross" required>
                <Input
                  id="ps-gross"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={draft.grossRupees}
                  required
                  onChange={(e) => set("grossRupees", e.target.value)}
                  placeholder="20000"
                />
              </Field>

              <Field label="Deductions (₹)" htmlFor="ps-deduction">
                <Input
                  id="ps-deduction"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={draft.deductionRupees}
                  onChange={(e) => set("deductionRupees", e.target.value)}
                  placeholder="0"
                />
              </Field>
            </div>

            {deductionExceedsGross ? (
              <p className="text-destructive text-xs">Deductions can&apos;t exceed gross pay.</p>
            ) : netPaise !== null ? (
              <div className="border-border/60 bg-muted/20 flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <span className="text-muted-foreground">Net pay</span>
                <span className="font-semibold tabular-nums">{formatPaise(netPaise)}</span>
              </div>
            ) : null}

            <Field label="Note" htmlFor="ps-note">
              <Textarea
                id="ps-note"
                value={draft.note}
                maxLength={2000}
                rows={2}
                onChange={(e) => set("note", e.target.value)}
                placeholder="Optional — bonus, arrears, LOP details, etc."
              />
            </Field>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              {isEdit ? "Save changes" : "Add payslip"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
